import type { FlowInfo, FlowStep } from "../types/flow";
import type { ResolvedApp } from "./index-types";
import { isPlainObject } from "./StudioScreenRenderer.utils.data";

export function adaptRawFlow(raw: unknown): FlowInfo | null {
 if (!raw || typeof raw !== "object") return null;

 const obj = raw as Record<string, unknown>;
 const id = typeof obj.id === "string" ? obj.id : null;
 const stepsRaw = Array.isArray(obj.steps) ? (obj.steps as unknown[]) : [];
 if (!id) return null;

 const [domain = id, name = ""] = id.split(".");
 const meta: Record<string, unknown> = isPlainObject(obj.meta)
 ? { ...obj.meta }
 : {};
 if (Object.prototype.hasOwnProperty.call(obj, "manifest") && meta.manifest === undefined) {
 meta.manifest = obj.manifest;
 }
 if (
 Object.prototype.hasOwnProperty.call(obj, "manifestPath") &&
 meta.manifestPath === undefined
 ) {
 meta.manifestPath = obj.manifestPath;
 }

 return {
 id,
 domain,
 name,
 params: {},
 steps: stepsRaw as readonly FlowStep[],
 callsOperations: [],
 callsFlows: [],
 triggeredByScreens: [],
 isStreaming: false,
 meta,
 sourcePath: "",
 };
}

/** Convert a camelCase/PascalCase name to kebab-case ("sidebarMount" -> "sidebar-mount"). */
function toKebabCase(name: string): string {
 return name
 .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
 .replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2")
 .toLowerCase();
}

/** Convert a kebab-case name to camelCase ("sidebar-mount" -> "sidebarMount"). */
function toCamelCase(name: string): string {
 return name
 .toLowerCase()
 .replace(/-+([a-z])/g, (_, character: string) => character.toUpperCase());
}

/**
 * Candidate registry keys for a flow id.
 *
 * Flow files on disk are commonly authored in kebab-case while steps and
 * atom triggers reference them in camelCase (e.g. `f:call id:
 * "shell.sidebar.mount"` vs flow id "shell.sidebar-mount"). The exact id is
 * tried first so existing authoring is unaffected; deterministic
 * case/hyphen variants and a bare lowercase form follow so mismatched
 * authoring still resolves instead of throwing "references unknown flow".
 */
function getFlowIdCandidates(id: string): string[] {
 const trimmed = id.trim();
 if (!trimmed) return [];

 const out: string[] = [];
 const push = (candidate: string | undefined): void => {
 if (candidate && candidate.length > 0 && !out.includes(candidate)) {
 out.push(candidate);
 }
 };

 push(trimmed);
 push(toKebabCase(trimmed));
 push(toCamelCase(trimmed));
 push(trimmed.toLowerCase());
 return out;
}

/** Missing or invalid registry entries are absent, not nullable flows. */
export function resolveFlowFromApp(app: ResolvedApp, id: string): FlowInfo | undefined {
 if (!id) return undefined;

 for (const candidate of getFlowIdCandidates(id)) {
 const raw = app.flows[candidate];
 if (isPlainObject(raw) && typeof raw.id === "string") {
 return adaptRawFlow(raw) ?? undefined;
 }
 }
 return undefined;
}